import React from "react";
import {
    Avatar,
    Box,
    Chip,
    InputAdornment,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import {Search} from "@mui/icons-material";
import type {Supplier360} from "../supplier360DemoData";
import {formatCompactCurrency, riskScoreBand, supplierStatusColor, supplierStatusLabel} from "../supplier360Utils";

type Props = {
    suppliers: Supplier360[];
    selectedId: string | null;
    onSelect: (id: string) => void;
};

export const SupplierListPanel: React.FC<Props> = ({suppliers, selectedId, onSelect}) => {
    const [query, setQuery] = React.useState('');

    const filtered = React.useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return suppliers;
        return suppliers.filter(s =>
            s.name.toLowerCase().includes(q) ||
            s.category.toLowerCase().includes(q) ||
            s.country.toLowerCase().includes(q)
        );
    }, [suppliers, query]);

    return <Paper variant="outlined" className="s360-list">
        <Box className="s360-list-search">
            <Typography variant="overline" color="text.secondary">
                Suppliers ({suppliers.length})
            </Typography>
            <TextField
                fullWidth
                size="small"
                placeholder="Search supplier, category, country"
                value={query}
                onChange={e => setQuery(e.target.value)}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search fontSize="small"/>
                            </InputAdornment>
                        ),
                    },
                }}
            />
        </Box>
        <Box className="s360-list-scroll">
            {filtered.length === 0
                ? <div className="s360-list-empty">No suppliers match “{query}”.</div>
                : <List disablePadding>
                    {filtered.map(s => {
                        const band = riskScoreBand(s.riskScore);
                        return <ListItemButton
                            key={s.id}
                            className={`s360-list-item${s.id === selectedId ? ' selected' : ''}`}
                            selected={s.id === selectedId}
                            onClick={() => onSelect(s.id)}>
                            <Avatar
                                sx={{bgcolor: s.logoColor, width: 36, height: 36, mr: 1.5, fontSize: 14}}
                                variant="rounded">
                                {s.name.slice(0, 2).toUpperCase()}
                            </Avatar>
                            <ListItemText
                                primary={
                                    <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                                        <span>{s.name}</span>
                                        <span className="s360-list-item-spend">
                                            {formatCompactCurrency(s.annualSpend, s.currency)}
                                        </span>
                                    </Box>
                                }
                                secondary={
                                    <Box display="flex" alignItems="center" gap={0.5} mt={0.5} flexWrap="wrap">
                                        <Typography variant="caption" color="text.secondary">
                                            {s.category} · {s.country}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            color={supplierStatusColor(s.status)}
                                            label={supplierStatusLabel(s.status)}
                                            sx={{height: 18, fontSize: 10}}
                                        />
                                        <Chip
                                            size="small"
                                            color={band.color}
                                            label={`Risk: ${band.label}`}
                                            sx={{height: 18, fontSize: 10}}
                                        />
                                    </Box>
                                }
                                slotProps={{secondary: {component: 'div'}}}
                            />
                        </ListItemButton>;
                    })}
                </List>
            }
        </Box>
    </Paper>;
};
